const MEASURED_STATUSES = new Set(["completed", "in_progress"]);

const normalizeText = (value) =>
  typeof value === "string" ? value.trim().toLocaleLowerCase() : "";

const normalizeCategory = (value) => {
  const category = normalizeText(value)
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (["input", "inputs"].includes(category)) return "input";
  if (["process", "processes"].includes(category)) return "processes";
  if ([
    "culture_and_climate",
    "climate_and_culture",
    "culture_climate",
    "climate_culture",
  ].includes(category)) return "culture_and_climate";
  return category;
};

const compareText = (left, right) =>
  normalizeText(left).localeCompare(normalizeText(right)) ||
  left.localeCompare(right);

const categoryLabel = (categoryKey) => {
  if (categoryKey === "input") return "Input";
  if (categoryKey === "processes") return "Processes";
  if (categoryKey === "culture_and_climate") return "Culture & Climate";
  return categoryKey
    ? categoryKey.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "";
};

const cleanIdentityValue = (value) =>
  value === null || value === undefined ? "" : value.toString().trim();

const prepareFactor = (factor, surveyId) => {
  if (!factor || typeof factor !== "object") return null;

  const nameKey = normalizeText(factor.name);
  if (!nameKey) return null;

  const categoryKey = normalizeCategory(factor.areaKey ?? factor.area);
  const factorId = cleanIdentityValue(factor.id);
  const sharedSurveyId = cleanIdentityValue(surveyId);

  return {
    name: factor.name.trim(),
    nameKey,
    categoryKey,
    categoryLabel:
      typeof factor.areaLabel === "string" && factor.areaLabel.trim()
        ? factor.areaLabel.trim()
        : categoryLabel(categoryKey),
    canonicalKey: factorId && sharedSurveyId
      ? `survey:${sharedSurveyId}|factor:${factorId}`
      : null,
    localIdKey: factorId ? `factor:${factorId}` : null,
    semanticKey: categoryKey
      ? `category:${categoryKey}|name:${nameKey}`
      : null,
  };
};

const factorsMatch = (factor, candidate) => {
  if (
    factor.canonicalKey &&
    candidate.canonicalKeys.has(factor.canonicalKey)
  ) return true;
  if (factor.semanticKey && candidate.semanticKeys.has(factor.semanticKey)) {
    return true;
  }
  return !factor.categoryKey &&
    candidate.hasUncategorizedFactor &&
    candidate.nameKeys.has(factor.nameKey);
};

const uniqueFactorsFor = (organization, mode) => {
  if (!MEASURED_STATUSES.has(organization.status)) return [];

  const source = mode === "strength"
    ? organization.strengths
    : organization.developmentAreas;
  if (!Array.isArray(source)) return [];

  const uniqueFactors = [];
  source.forEach((factor) => {
    const prepared = prepareFactor(factor, organization.surveyId);
    if (!prepared) return;

    const duplicate = uniqueFactors.some((candidate) =>
      (prepared.localIdKey && prepared.localIdKey === candidate.localIdKey) ||
      (prepared.semanticKey && prepared.semanticKey === candidate.semanticKey) ||
      (!prepared.categoryKey &&
        !candidate.categoryKey &&
        prepared.nameKey === candidate.nameKey)
    );
    if (!duplicate) uniqueFactors.push(prepared);
  });
  return uniqueFactors;
};

const prepareOrganization = (organization, index) => {
  if (!organization || typeof organization !== "object") return null;

  const fallbackId = organization.organizationId ?? organization.id ?? index;
  const organizationName =
    typeof organization.organizationName === "string" &&
    organization.organizationName.trim()
      ? organization.organizationName.trim()
      : "School";

  return {
    ...organization,
    id: organization.id ?? fallbackId,
    organizationId: organization.organizationId ?? fallbackId,
    organizationName,
  };
};

export const buildNetworkPairingGroups = (
  organizations = [],
  mode = "development"
) => {
  const preparedOrganizations = (Array.isArray(organizations) ? organizations : [])
    .map(prepareOrganization)
    .filter(Boolean);
  const groups = [];
  const organizationIdsWithReportData = new Set();

  preparedOrganizations.forEach((organization) => {
    const hasReportData = uniqueFactorsFor(organization, "development").length > 0 ||
      uniqueFactorsFor(organization, "strength").length > 0;
    if (hasReportData) organizationIdsWithReportData.add(organization.id);

    const factors = uniqueFactorsFor(organization, mode);

    factors.forEach((factor) => {
      let group = groups.find((candidate) => factorsMatch(factor, candidate));
      if (!group) {
        group = {
          key: `factor-group-${groups.length}`,
          name: factor.name,
          categoryKey: factor.categoryKey,
          categoryLabel: factor.categoryLabel,
          stabilityValue: null,
          canonicalKeys: new Set(),
          semanticKeys: new Set(),
          nameKeys: new Set(),
          hasUncategorizedFactor: false,
          organizations: [],
          organizationIds: new Set(),
        };
        groups.push(group);
      }

      if (factor.canonicalKey) group.canonicalKeys.add(factor.canonicalKey);
      if (factor.semanticKey) group.semanticKeys.add(factor.semanticKey);
      group.nameKeys.add(factor.nameKey);
      if (!factor.categoryKey) group.hasUncategorizedFactor = true;

      if (!group.organizationIds.has(organization.id)) {
        group.organizationIds.add(organization.id);
        group.organizations.push(organization);
      }
    });
  });

  return {
    groups: groups
      .map((group) => {
        const factorIdentity = [...group.canonicalKeys].sort()[0] ||
          [...group.semanticKeys].sort()[0] ||
          `name:${[...group.nameKeys].sort()[0]}`;

        return {
          key: factorIdentity,
          factorIdentity,
          name: group.name,
          categoryKey: group.categoryKey,
          categoryLabel: group.categoryLabel,
          stabilityValue: group.stabilityValue,
          organizations: group.organizations.sort((left, right) =>
            compareText(left.organizationName, right.organizationName) ||
            left.id.toString().localeCompare(right.id.toString())
          ),
          organizationCount: group.organizationIds.size,
        };
      })
      .sort((left, right) =>
        right.organizationCount - left.organizationCount ||
        compareText(left.name, right.name) ||
        left.key.localeCompare(right.key)
      ),
    organizationCount: preparedOrganizations.length,
    organizationsWithReportData: organizationIdsWithReportData.size,
  };
};

export const filterNetworkPairingGroups = (groups = [], searchText = "") => {
  const query = normalizeText(searchText);
  if (!query) return groups;

  return groups.filter((group) =>
    normalizeText(group.name).includes(query) ||
    normalizeText(group.categoryLabel).includes(query) ||
    group.organizations.some((organization) =>
      normalizeText(organization.organizationName).includes(query)
    )
  );
};
